import type { TFunction } from 'i18next';

import type { TAttributionModel } from '@feathr/blackbox';

/**
 * The six campaign-type categories the Conversions Dashboard filters by. These
 * are higher-level groupings than `CampaignClass`; the mapping from category
 * to concrete `CampaignClass` lives at the data layer (#7898/#7899).
 */
export type TCampaignTypeFilter =
  | 'ads'
  | 'email'
  | 'invite'
  | 'tracked_link'
  | 'conversation'
  | 'landing_page';

export interface ICampaignTypeOption {
  id: TCampaignTypeFilter;
  name: string;
}

const CAMPAIGN_TYPE_LABEL_KEYS: Record<TCampaignTypeFilter, string> = {
  ads: 'Ads',
  email: 'Email',
  invite: 'Invite',
  tracked_link: 'Tracked link',
  conversation: 'Conversation',
  landing_page: 'Landing page',
};

export const CAMPAIGN_TYPE_FILTERS = Object.keys(
  CAMPAIGN_TYPE_LABEL_KEYS,
) as readonly TCampaignTypeFilter[];

export function buildCampaignTypeOptions(t: TFunction): ICampaignTypeOption[] {
  return CAMPAIGN_TYPE_FILTERS.map((id) => ({ id, name: t(CAMPAIGN_TYPE_LABEL_KEYS[id]) }));
}

export function isCampaignTypeFilter(value: string): value is TCampaignTypeFilter {
  return (CAMPAIGN_TYPE_FILTERS as readonly string[]).includes(value);
}

export interface IFiltersState {
  attr: TAttributionModel;
  projects: readonly string[];
  types: readonly TCampaignTypeFilter[];
}

export const DEFAULT_FILTERS = {
  attr: 'last_touch',
  projects: [],
  types: [],
} as const satisfies IFiltersState;

export function activeFilterCount(filters: IFiltersState): number {
  let count = 0;
  if (filters.attr !== DEFAULT_FILTERS.attr) {
    count++;
  }
  if (filters.projects.length > 0) {
    count++;
  }
  if (filters.types.length > 0) {
    count++;
  }
  return count;
}

export function filtersEqual(a: IFiltersState, b: IFiltersState): boolean {
  if (a.attr !== b.attr) {
    return false;
  }
  if (a.projects.length !== b.projects.length) {
    return false;
  }
  if (a.types.length !== b.types.length) {
    return false;
  }
  if (a.projects.some((id, i) => id !== b.projects[i])) {
    return false;
  }
  if (a.types.some((id, i) => id !== b.types[i])) {
    return false;
  }
  return true;
}
